/**
 * Every student- and phone-facing API call, in one place.
 *
 * Built directly against docs/API.md and the zod schemas in
 * `@exam/shared/contracts`. No screen calls `fetch` or `@/lib/api` itself, so a
 * contract change lands in this file only.
 *
 * Two auth modes, exactly as the doc describes:
 *   - Laptop: the `ep_session` cookie (the `api` helper always sends it).
 *   - Phone:  no session. It claims a pair code, gets a `device_token`, and
 *     sends it as `X-Device-Token` on heartbeat, proctor and media calls.
 */
import {
  AttemptResultResponse,
  AttemptStateResponse,
  AttemptTimerResponse,
  ClaimPairCodeResponse,
  DevicesResponse,
  HeartbeatResponse,
  IssuePairCodeResponse,
  MyExamsResponse,
  ProctorIngestResponse,
  ProctorSummaryResponse,
  SaveAnswerResponse,
  SetStageResponse,
  UploadChunkResponse,
  UploadPhotoResponse,
  type AttemptMediaKind,
  type AttemptStage,
  type DeviceRole,
  type ProctorEventInput,
  type RecordVisitInput,
  type RecordingSource,
  type SaveAnswerInput,
} from "@exam/shared";
import { z } from "zod";
import { ApiRequestError, api } from "@/lib/api";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export const DEVICE_TOKEN_HEADER = "X-Device-Token";

/**
 * A binary upload that never returns (connection open, no response) would pin
 * the drain loop forever and make submit's "finishing upload" hang, so we abort
 * a stuck request and let the uploader re-queue it. But the timeout is ADAPTIVE
 * to the payload size: a fixed 30s killed slow-but-working mobile uploads (a
 * 300-600 KB chunk over a weak uplink legitimately takes minutes), which turned
 * a slow link into a permanent zero. We budget a very slow floor of ~2 KB/s and
 * clamp to a sane range, so only a genuinely dead connection is aborted.
 */
const UPLOAD_MIN_BYTES_PER_SEC = 2_000; // ~16 kbps — a terrible-but-alive uplink
const UPLOAD_TIMEOUT_FLOOR_MS = 60_000;
const UPLOAD_TIMEOUT_CEIL_MS = 240_000;

function uploadTimeoutFor(bytes: number): number {
  const budget = (bytes / UPLOAD_MIN_BYTES_PER_SEC) * 1000;
  return Math.min(
    UPLOAD_TIMEOUT_CEIL_MS,
    Math.max(UPLOAD_TIMEOUT_FLOOR_MS, Math.round(budget)),
  );
}

/**
 * Responses are validated against the shared schemas so contract drift shows up
 * as one loud console error naming the offending field, rather than as an
 * undefined deep inside a component.
 *
 * On a mismatch we log and return the payload anyway. That is deliberate: an
 * extra or renamed field on a poll response must not throw a student out of a
 * live exam. Genuine breakage still surfaces — loudly, and in one place.
 */
function parse<T extends z.ZodTypeAny>(
  schema: T,
  payload: unknown,
  label: string,
): z.infer<T> {
  const result = schema.safeParse(payload);
  if (result.success) return result.data;

  console.error(
    `[exam-api] ${label} did not match its contract:`,
    result.error.issues
      .map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`)
      .join("; "),
  );
  return payload as z.infer<T>;
}

/**
 * Request options for a call that may be device-authenticated.
 *
 * `credentials: "omit"` is essential, not tidiness. If a session cookie is
 * present the API authenticates by session and IGNORES the device token — so a
 * phone page opened in the same browser profile as the signed-in student is
 * rejected with `403 "Only the paired phone can send a phone heartbeat"`. A
 * real phone has no cookie and works by luck; this makes it work by design.
 */
function deviceAuth(deviceToken?: string) {
  return deviceToken
    ? {
        headers: { [DEVICE_TOKEN_HEADER]: deviceToken },
        credentials: "omit" as const,
      }
    : {};
}

/**
 * Binary uploads bypass the JSON helper: the blob is the raw request body and
 * every parameter travels in the query string, so the Worker can stream a
 * single `R2.put` without buffering or base64 inflation (docs/API.md §5).
 */
async function postBinary<T extends z.ZodTypeAny>(
  path: string,
  blob: Blob,
  query: Record<string, string | number | undefined>,
  schema: T,
  label: string,
  deviceToken?: string,
): Promise<z.infer<T>> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.set(k, String(v));
  }

  const controller = new AbortController();
  // The timer must outlive the fetch and cover reading the BODY too. It used
  // to be cleared as soon as the headers arrived, which left `res.json()`
  // below unbounded — a response whose body never finishes streaming hung the
  // uploader's drain loop for the rest of the exam, with no error to show for
  // it. Cleared in the `finally` that wraps both, instead.
  const timer = setTimeout(
    () => controller.abort(),
    uploadTimeoutFor(blob.size),
  );

  try {
    let res: Response;
    try {
      res = await fetch(`${BASE_URL}${path}?${params}`, {
        method: "POST",
        // Same rule as deviceAuth above: a session cookie would take
        // precedence over the device token and the upload would be 403'd.
        credentials: deviceToken ? "omit" : "include",
        headers: {
          "Content-Type": blob.type || "application/octet-stream",
          ...(deviceToken ? { [DEVICE_TOKEN_HEADER]: deviceToken } : {}),
        },
        body: blob,
        signal: controller.signal,
      });
    } catch {
      throw new ApiRequestError("internal", 0, "Network request failed");
    }

    if (!res.ok) {
      // Mirror the JSON client so callers can branch on `code` identically.
      let message = `Upload failed (${res.status})`;
      let code: "conflict" | "forbidden" | "bad_request" | "internal" =
        "internal";
      try {
        const body = (await res.json()) as {
          error?: { code?: string; message?: string };
        };
        if (body.error?.message) message = body.error.message;
        if (body.error?.code === "conflict") code = "conflict";
        else if (body.error?.code === "forbidden") code = "forbidden";
        else if (body.error?.code === "bad_request") code = "bad_request";
      } catch {
        /* non-JSON error body */
      }
      throw new ApiRequestError(code, res.status, message);
    }

    return parse(schema, await res.json(), label);
  } finally {
    clearTimeout(timer);
  }
}

/* ------------------------------------------------------------------ */
/* Exams                                                               */
/* ------------------------------------------------------------------ */

export async function listMyExams(): Promise<MyExamsResponse> {
  return parse(
    MyExamsResponse,
    await api.get<unknown>("/api/exams/mine"),
    "GET /api/exams/mine",
  );
}

/* ------------------------------------------------------------------ */
/* Attempts                                                            */
/* ------------------------------------------------------------------ */

/**
 * Idempotent. Creates (or returns) the `not_started` attempt row and the
 * `laptop` device session, so the phone has something to pair against. Does not
 * start the timer — that is `startAttempt`.
 */
export async function prepareAttempt(
  examId: string,
): Promise<AttemptStateResponse> {
  return parse(
    AttemptStateResponse,
    await api.post<unknown>("/api/attempts/prepare", {
      json: { exam_id: examId },
    }),
    "POST /api/attempts/prepare",
  );
}

/**
 * Starts the clock. Rejects with `conflict` if the phone is not paired and
 * beating — the message is written for the student and can be shown verbatim.
 */
export async function startAttempt(
  examId: string,
): Promise<AttemptStateResponse> {
  return parse(
    AttemptStateResponse,
    await api.post<unknown>("/api/attempts", { json: { exam_id: examId } }),
    "POST /api/attempts",
  );
}

/**
 * The laptop's one poll: timer, answers, review flags, media counts, stage and
 * device health.
 *
 * Pass `includePaper: false` once the paper is in hand. This runs every few
 * seconds for up to three hours, and the paper is by far the expensive part of
 * the response — there is no reason to re-send a dozen questions of markdown
 * that cannot change mid-attempt.
 */
export async function getAttempt(
  attemptId: string,
  options: { includePaper?: boolean } = {},
): Promise<AttemptStateResponse> {
  const includePaper = options.includePaper ?? true;
  return parse(
    AttemptStateResponse,
    await api.get<unknown>(`/api/attempts/${attemptId}`, {
      query: { paper: includePaper ? "1" : "0" },
    }),
    "GET /api/attempts/:id",
  );
}

/** Cheap poll — one row read. This is what drives the countdown. */
export async function getTimer(
  attemptId: string,
): Promise<AttemptTimerResponse> {
  return parse(
    AttemptTimerResponse,
    await api.get<unknown>(`/api/attempts/${attemptId}/timer`),
    "GET /api/attempts/:id/timer",
  );
}

/**
 * `time_spent_ms` is a DELTA — the server accumulates it (docs/API.md §3).
 * Sending a total would inflate the recorded time on every save.
 *
 * `visit_offsets_ms` is likewise incremental: only the visits recorded since
 * the last successful save for this question. The server appends them.
 */
export async function saveAnswer(
  attemptId: string,
  input: SaveAnswerInput,
): Promise<SaveAnswerResponse> {
  return parse(
    SaveAnswerResponse,
    await api.post<unknown>(`/api/attempts/${attemptId}/answers`, {
      json: input,
    }),
    "POST /api/attempts/:id/answers",
  );
}

/**
 * Records that the student *opened* a question without changing the answer, so
 * the admin's review timeline is complete for questions they only read.
 *
 * `offset_ms` must come from the server-anchored clock (see lib/exam/serverClock)
 * measured from `attempts.started_at` — the same t=0 as the recording chunks
 * and proctor events. Never `Date.now()` arithmetic.
 */
export async function recordVisit(
  attemptId: string,
  input: RecordVisitInput,
): Promise<SaveAnswerResponse> {
  return parse(
    SaveAnswerResponse,
    await api.post<unknown>(`/api/attempts/${attemptId}/visits`, {
      json: input,
    }),
    "POST /api/attempts/:id/visits",
  );
}

export async function submitAttempt(
  attemptId: string,
): Promise<AttemptResultResponse> {
  return parse(
    AttemptResultResponse,
    await api.post<unknown>(`/api/attempts/${attemptId}/submit`),
    "POST /api/attempts/:id/submit",
  );
}

export async function getResult(
  attemptId: string,
): Promise<AttemptResultResponse> {
  return parse(
    AttemptResultResponse,
    await api.get<unknown>(`/api/attempts/${attemptId}/result`),
    "GET /api/attempts/:id/result",
  );
}

/* ------------------------------------------------------------------ */
/* Pairing                                                             */
/* ------------------------------------------------------------------ */

/** Creates or rotates the phone's 6-digit code. Rotating unbinds any phone. */
export async function issuePairCode(
  attemptId: string,
): Promise<IssuePairCodeResponse> {
  return parse(
    IssuePairCodeResponse,
    await api.post<unknown>(`/api/attempts/${attemptId}/pair`),
    "POST /api/attempts/:id/pair",
  );
}

/** The only public write in the API. Called by the phone, once. */
export async function claimPairCode(
  pairCode: string,
): Promise<ClaimPairCodeResponse> {
  return parse(
    ClaimPairCodeResponse,
    await api.post<unknown>("/api/attempts/pair/claim", {
      json: { pair_code: pairCode },
    }),
    "POST /api/attempts/pair/claim",
  );
}

/**
 * Both devices beat every HEARTBEAT_INTERVAL_MS. A `phone` beat must carry the
 * device token; a `laptop` beat uses the session cookie.
 */
export async function heartbeat(
  attemptId: string,
  role: DeviceRole,
  deviceToken?: string,
): Promise<HeartbeatResponse> {
  return parse(
    HeartbeatResponse,
    await api.post<unknown>(`/api/attempts/${attemptId}/heartbeat`, {
      json: { role },
      ...deviceAuth(deviceToken),
    }),
    "POST /api/attempts/:id/heartbeat",
  );
}

/**
 * The laptop is the orchestrator: it sets the stage, and the phone renders
 * whatever it is handed on its next heartbeat. The phone never advances this
 * itself, and a stage change must never interrupt phone recording or uploads.
 */
export async function setStage(
  attemptId: string,
  stage: AttemptStage,
): Promise<SetStageResponse> {
  return parse(
    SetStageResponse,
    await api.post<unknown>(`/api/attempts/${attemptId}/stage`, {
      json: { stage },
    }),
    "POST /api/attempts/:id/stage",
  );
}

export async function getDevices(
  attemptId: string,
): Promise<DevicesResponse> {
  return parse(
    DevicesResponse,
    await api.get<unknown>(`/api/attempts/${attemptId}/devices`),
    "GET /api/attempts/:id/devices",
  );
}

/* ------------------------------------------------------------------ */
/* Proctor                                                             */
/* ------------------------------------------------------------------ */

export async function sendProctorEvents(
  attemptId: string,
  events: ProctorEventInput[],
  deviceToken?: string,
): Promise<ProctorIngestResponse> {
  return parse(
    ProctorIngestResponse,
    await api.post<unknown>(`/api/proctor/${attemptId}/events`, {
      json: { events },
      ...deviceAuth(deviceToken),
    }),
    "POST /api/proctor/:id/events",
  );
}

export async function getProctorSummary(
  attemptId: string,
): Promise<ProctorSummaryResponse> {
  return parse(
    ProctorSummaryResponse,
    await api.get<unknown>(`/api/proctor/${attemptId}/summary`),
    "GET /api/proctor/:id/summary",
  );
}

/* ------------------------------------------------------------------ */
/* Media                                                               */
/* ------------------------------------------------------------------ */

export interface ChunkUpload {
  attemptId: string;
  source: RecordingSource;
  chunkIndex: number;
  /**
   * Which MediaRecorder session produced it. Only the first blob of a session
   * carries the WebM header, so the admin player must not concatenate across
   * a change of this.
   */
  sessionIdx: number;
  startedMs: number;
  durationMs: number;
  blob: Blob;
  /** Required when `source` is "phone". */
  deviceToken?: string;
}

/** Idempotent per (attempt, source, chunk_index) — safe to retry forever. */
export function uploadChunk(u: ChunkUpload): Promise<UploadChunkResponse> {
  return postBinary(
    `/api/media/attempts/${u.attemptId}/chunks`,
    u.blob,
    {
      source: u.source,
      chunk_index: u.chunkIndex,
      session_idx: u.sessionIdx,
      started_ms: u.startedMs,
      duration_ms: u.durationMs,
    },
    UploadChunkResponse,
    "POST /api/media/attempts/:id/chunks",
    u.deviceToken,
  );
}

/* ------------------------------------------------------------------ */
/* phone viewfinder                                                    */
/* ------------------------------------------------------------------ */

/** Phone → server: the newest frame, overwriting the last one. */
export function publishViewfinderFrame(
  attemptId: string,
  blob: Blob,
  deviceToken: string,
): Promise<{ ok: boolean }> {
  return postBinary(
    `/api/media/attempts/${attemptId}/viewfinder`,
    blob,
    {},
    z.object({ ok: z.boolean() }),
    "POST /api/media/attempts/:id/viewfinder",
    deviceToken,
  );
}

export interface ViewfinderFrame {
  /** Object URL. The caller MUST revoke it, or a frame a second leaks. */
  url: string;
  /** How old the frame is server-side, or null if it could not be read. */
  ageMs: number | null;
  /** Identity of these bytes; pass back to detect an unchanged frame. */
  etag: string | null;
}

export type ViewfinderResult =
  /** The phone has not published anything yet. */
  | { kind: "none" }
  /** Same bytes as the etag passed in — the phone has sent nothing new. */
  | { kind: "unchanged" }
  | { kind: "frame"; frame: ViewfinderFrame };

/**
 * Laptop → server: the phone's latest frame.
 *
 * Fetched rather than pointed at with `<img src>` because the API may be on
 * another origin, where an `<img>` would send no cookie and 401 — and because
 * the freshness signals are only readable this way.
 *
 * Freshness is reported two ways on purpose. The age header is exact but is
 * a custom header, so it is only readable cross-origin while the API's CORS
 * `exposeHeaders` lists it; if that is ever dropped the panel would silently
 * lose its staleness signal in production while passing every same-origin
 * test in dev. `unchanged` needs no such cooperation: an ETag that has not
 * moved means the phone has published nothing since, whatever the headers
 * say.
 *
 * This matters because the frame is stored, not streamed. When the phone
 * stops publishing the server keeps serving the last frame indefinitely, so
 * "the request succeeded" says nothing at all about whether the picture is
 * current.
 */
export async function fetchViewfinderFrame(
  attemptId: string,
  { etag, signal }: { etag?: string | null; signal?: AbortSignal } = {},
): Promise<ViewfinderResult> {
  const res = await fetch(
    `${BASE_URL}/api/media/attempts/${attemptId}/viewfinder`,
    {
      credentials: "include",
      cache: "no-store",
      signal,
      headers: etag ? { "If-None-Match": etag } : undefined,
    },
  );
  if (res.status === 404) return { kind: "none" };
  if (res.status === 304) return { kind: "unchanged" };
  if (!res.ok) throw new Error(`Viewfinder frame failed (${res.status})`);

  const raw = res.headers.get("X-Captured-Age-Ms");
  const ageMs = raw !== null && /^\d+$/.test(raw) ? Number(raw) : null;
  return {
    kind: "frame",
    frame: {
      url: URL.createObjectURL(await res.blob()),
      ageMs,
      etag: res.headers.get("ETag"),
    },
  };
}

/** Placement confirmed — stop relaying and drop the stored frame. */
export async function clearViewfinder(attemptId: string): Promise<void> {
  await fetch(`${BASE_URL}/api/media/attempts/${attemptId}/viewfinder`, {
    method: "DELETE",
    credentials: "include",
  });
}

export interface PhotoUpload {
  attemptId: string;
  kind: AttemptMediaKind;
  blob: Blob;
  deviceToken?: string;
}

export function uploadPhoto(u: PhotoUpload): Promise<UploadPhotoResponse> {
  return postBinary(
    `/api/media/attempts/${u.attemptId}/photos`,
    u.blob,
    { kind: u.kind },
    UploadPhotoResponse,
    "POST /api/media/attempts/:id/photos",
    u.deviceToken,
  );
}
